import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { makeStyles, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, IconButton, Tooltip, Box } from "@material-ui/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { BoundsInInches, FieldModel, Group } from "./types";
import { createFieldsPlugin } from "./createFieldsPlugin";
import { Inspector } from "./inspector";
import PathTree from "../../DocumentType/components/detalle/pathTree";
import { FieldTypeBffAnalize } from "../utils/parseFields";
import { useDispatch, useSelector } from "react-redux";
import { getDocTypeList1 } from "../../../store/docType/actions";
import { useNavigate } from "react-router-dom";
import { DocumentLoadEvent } from "@react-pdf-viewer/core";
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';
import type { RenderThumbnailItemProps } from '@react-pdf-viewer/thumbnail';
import './styles.css'
import { ThumbnailIcon } from '@react-pdf-viewer/default-layout';
import appendIcon from "../../../assets/append.png";
import deleteIcon from "../../../assets/delete.png";
import reorderIcon from "../../../assets/reorder.png";
import moveUpIcon from "../../../assets/bup.png";
import moveDownIcon from "../../../assets/bdown.png";
import dragIcon from "../../../assets/dragindicator.png";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";



const PDFJS_VERSION = "2.16.105"; // keep in sync with your package.json
interface RootState {
	fileReducer: {
		uploadResponse: {
			fields: Array<FieldTypeBffAnalize>;
			highlighted_pdf_url: string;
			ai_insights: string;
		};
		selectedFile: File | null;
	};
	Tree: {
		responseTree: any;
		loadingTree: boolean;
	};
}


// ------------------------
// Main Document Editor Component
// ------------------------

interface DocumentEditorProps {
	fileUrl: string;
	templateName?: string;
	templateId?: string;
	fields: FieldModel[];
	setFields: (updater: (prev: FieldModel[]) => FieldModel[]) => void;
	showInspector?: boolean;
	selectedFolder?: string;
	setSelectedFolder?: (folder: string) => void;
	selectedFolderDisplay?: string;
	setSelectedFolderDisplay?: (nameOrPath: string) => void;
	currentPage?: number;
	setCurrentPage?: (page: number) => void;
	onContextMenu?: (event: React.MouseEvent, fieldIndex: number, fieldId?: string) => void;
	onDuplicateField?: (fieldId: string) => void;
	onAppendPage?: (afterPage?: number) => void;
	onDeletePage?: (page?: number | number[]) => void;
	onMovePage?: (pageOrDirection: number | "up" | "down" | { action: "first" | "last" | "page"; page?: number; sourcePage?: number }) => void;
	onNumPages?: (num: number) => void;
	onOpenDocumentModal?: () => void;
	fieldAssignments?: Record<string, string>;
	onFieldSelect?: (fieldId: string) => void;
	groups?: Group[];
	onGroupsChange?: (groups: Group[]) => void;
	onFilesDrop?: (files: File[], useAi: boolean) => void;
	placingField?: boolean;
	onPlaceField?: (payload: { page: number; x: number; y: number }) => void;
	highlightedFieldId?: string | null;
	onRequestAddField?: () => void;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
	fileUrl,
	templateName,
	templateId,
	fields,
	setFields,
	showInspector = true,
	selectedFolder,
	setSelectedFolder,
	selectedFolderDisplay,
	setSelectedFolderDisplay,
	currentPage = 1,
	setCurrentPage,
	onContextMenu,
	onDuplicateField,
	onAppendPage,
	onDeletePage,
	onMovePage,
	onNumPages,
	fieldAssignments = {},
	onFieldSelect,
	groups = [],
	onGroupsChange,
	onFilesDrop,
	placingField = false,
	onPlaceField,
	highlightedFieldId = null,
	onRequestAddField,
}) => {
	const useStyles = makeStyles((theme) => ({
		documentEditorContainer: {
			flexDirection: "row",
			gap: 16,
			height: "calc(100vh - 200px)", // Adjust based on header height
			gridTemplateColumns: showInspector ? "6fr 3fr" : "1fr", // 2 columns: PDF viewer and inspector
			display: "grid",
			overflow: "hidden", // Prevent container from scrolling
			[theme.breakpoints.down("lg")]: {
				// Keep inspector on the right for smaller screens
				gridTemplateColumns: showInspector ? "minmax(0, 1fr) 360px" : "1fr",
				height: "calc(100vh - 200px)",
				overflow: "hidden",
				gap: 12,
			},
			[theme.breakpoints.down("md")]: {
				gridTemplateColumns: showInspector ? "minmax(0, 1fr) 320px" : "1fr",
				height: "calc(100vh - 200px)",
				overflow: "hidden",
				gap: 10,
			},
		},
		thumbnailsContainer: {

		},
		pdfViewerContainer: {
			flex: 1,
			position: "relative",
			overflow: "auto", // Enable scrolling for PDF viewer
			height: "100%",
			border: "1px solid #e0e0e0",
			borderRadius: "4px",
			[theme.breakpoints.down("lg")]: {
				height: "100%",
				minHeight: 0,
				minWidth: 0, // allow the grid column to shrink
			},
			[theme.breakpoints.down("md")]: {
				height: "100%",
				minHeight: 0,
				minWidth: 0,
			},
		},
		inspectorSectionContainer: {
			display: "flex",
			flexDirection: "column",
			height: "100%",
			overflow: "auto",
			[theme.breakpoints.down("lg")]: {
				height: "100%",
				overflow: "auto",
			},
			[theme.breakpoints.down("md")]: {
				height: "100%",
				overflow: "auto",
			},
		},
		helperText: {
			fontSize: "12px",
			backgroundColor: "#607d8b3d",
			padding: "5px",
			borderRadius: "3px",
			width: "100%",
			display: "flex",
		},
		inspectorContainer: {
			width: "100%",
			flex: 1,
			backgroundColor: "white",
			display: "flex",
			flexDirection: "column",
			justifyContent: "flex-start",
			padding: "12px",
			overflowY: "scroll", // Always show vertical scrollbar for inspector
			overflowX: "hidden", // Hide horizontal scrollbar
			border: "1px solid #e0e0e0",
			borderRadius: "4px",
			height: "auto",
			[theme.breakpoints.down("lg")]: {
				width: "100%",
				height: "auto",
				overflowY: "auto",
				padding: "10px",
			},
			[theme.breakpoints.down("md")]: {
				width: "100%",
				height: "auto",
				overflowY: "auto",
				padding: "10px",
			},
		},
		pathTreeContainer: {
			width: "100%",
			backgroundColor: "white",
			padding: "12px",
			maxHeight: "300px", // Limit height of path tree
			overflow: "auto", // Enable scrolling for path tree
			border: "1px solid #e0e0e0",
			borderRadius: "4px",
			marginBottom: 16,
			[theme.breakpoints.down("lg")]: {
				width: "100%",
				maxHeight: "none",
				padding: "10px",
			},
			[theme.breakpoints.down("md")]: {
				width: "100%",
				maxHeight: "none",
				padding: "10px",
			},
		},
		saveToCard: {
			border: "1px solid rgba(0,0,0,0.10)",
			borderRadius: 10,
			padding: 12,
			background: "#fff",
		},
		saveToHeader: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 8,
			marginBottom: 10,
		},
		requiredDot: {
			display: "inline-block",
			width: 8,
			height: 8,
			borderRadius: "50%",
			background: "#d32f2f",
			marginLeft: 6,
			verticalAlign: "middle",
		},
		saveToEmpty: {
			border: "1px dashed rgba(0,0,0,0.25)",
			borderRadius: 10,
			padding: 16,
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			minHeight: 72,
			background: "rgba(0,0,0,0.02)",
		},
		saveToFilledRow: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 10,
		},
		folderAvatar: {
			width: 36,
			height: 36,
			borderRadius: 10,
			background: "rgba(0, 106, 198, 0.12)",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0,
		},
		folderText: {
			minWidth: 0,
			flex: 1,
		},
		actionIcons: {
			display: "flex",
			gap: 6,
			alignItems: "center",
			flexShrink: 0,
		},
		emptyInspector: {
			padding: "12px",
			border: "1px dashed rgba(0,0,0,0.18)",
			borderRadius: 8,
			background: "rgba(0,0,0,0.02)",
		},
		emptyInspectorList: {
			marginTop: 10,
			display: "flex",
			flexDirection: "column",
			gap: 6,
		},

	}));
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [showFolderId, setShowFolderId] = useState<boolean>(false);
	// scale state removed (unused)
	const viewerContainerRef = useRef<HTMLDivElement>(null);
	const classes = useStyles();
	const [emptyPath] = useState(false);
	const [selectedCount, setSelectedCount] = useState(0);

	const selectedField = useMemo(
		() => fields.find((f) => f.id === selectedId),
		[fields, selectedId]
	);

	const openFolderPicker = useCallback(() => {
		const openEl = document.getElementById("template-builder-default-folder-open") as HTMLElement | null;
		openEl?.click();
	}, []);

	// Suppress ResizeObserver warnings
	useEffect(() => {
		const originalError = console.error;
		console.error = (...args) => {
			if (
				typeof args[0] === 'string' &&
				args[0].includes('ResizeObserver loop completed with undelivered notifications')
			) {
				// Ignore ResizeObserver warnings
				return;
			}
			originalError.apply(console, args);
		};

		return () => {
			console.error = originalError;
		};
	}, []);

	// Cleanup auto-scroll on unmount
	useEffect(() => {
		return () => {
			if (autoScrollIntervalRef.current) {
				clearInterval(autoScrollIntervalRef.current);
			}
		};
	}, []);

	const layout = defaultLayoutPlugin({
		renderToolbar: (Toolbar) => (
			<Toolbar>
				{(slots) => {
					const {
						CurrentPageInput,
						Download,
						EnterFullScreen,
						GoToFirstPage,
						GoToLastPage,
						GoToNextPage,
						GoToPreviousPage,
						NumberOfPages,
						Print,
						ShowProperties,
						ShowSearchPopover,
						Zoom,
						ZoomIn,
						ZoomOut,
					} = slots;
					return (
						<div
							style={{
								alignItems: 'center',
								display: 'flex',
								width: '100%',
							}}
						>
							{/* Search */}
							<div style={{ padding: '0px 2px' }}>
								<ShowSearchPopover />
							</div>
							<div style={{ width: 8 }} />
							<div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.12)' }} />
							<div style={{ width: 8 }} />

							{/* Zoom */}
							<div style={{ padding: '0px 2px' }}>
								<ZoomOut />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<Zoom />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<ZoomIn />
							</div>
							<div style={{ width: 8 }} />
							<div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.12)' }} />
							<div style={{ width: 8 }} />

							{/* Navigation */}
							<div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
								<GoToFirstPage />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<GoToPreviousPage />
							</div>
							<div style={{ padding: '0px 2px', width: '4rem' }}>
								<CurrentPageInput />
							</div>
							<div style={{ padding: '0px 2px' }}>
								/ <NumberOfPages />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<GoToNextPage />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<GoToLastPage />
							</div>
							<div style={{ width: 8 }} />
							<div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.12)' }} />
							<div style={{ width: 8 }} />

							{/* Export / Other */}
							<div style={{ padding: '0px 2px', marginLeft: 'auto' }}>
								<EnterFullScreen />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<Download />
							</div>
							<div style={{ padding: '0px 2px' }}>
								<Print />
							</div>
							<div style={{ padding: '0px 2px', marginRight: '10px' }}>
								<ShowProperties />
							</div>
						</div>
					);
				}}
			</Toolbar>
		),
		sidebarTabs: (defaultTabs) => [
			{
				content: <>
					<div className="thumbnailsContainer">
						<div className="upperToolbar">
							<span className="pagesText">Pages</span>
							{selectedCount > 0 &&
							<span className="selectedIndicator">{selectedCount} selected</span>
							}
							<img className="toolbarIcon" onClick={() => onAppendPage && onAppendPage()} src={appendIcon} alt="Append" />

						</div>
						<div className="lowerToolbar">
							<div className="toolbarLeft">
								<img
									className={`toolbarButton ${selectedCount === 1 ? '' : 'disabled'}`}
									onClick={() => {
										if (selectedCount === 1 && onMovePage) {
											// Find the selected page index (0-based) and convert to 1-based
											const selectedPageIndex = selectedPages.findIndex(isSelected => isSelected);
											if (selectedPageIndex !== -1) {
												const selectedPageNumber = selectedPageIndex + 1;
												onMovePage({ action: "page", page: selectedPageNumber - 1, sourcePage: selectedPageNumber });
											}
										}
									}}
									title={selectedCount === 1 ? "Move selected page up" : "Select exactly one page to move up"}
									src={moveUpIcon}
									alt="Move Up"
									style={{
										opacity: selectedCount === 1 ? 1 : 0.5,
										cursor: selectedCount === 1 ? 'pointer' : 'not-allowed'
									}}
								/>
								<img
									className={`toolbarButton ${selectedCount === 1 ? '' : 'disabled'}`}
									onClick={() => {
										if (selectedCount === 1 && onMovePage) {
											// Find the selected page index (0-based) and convert to 1-based
											const selectedPageIndex = selectedPages.findIndex(isSelected => isSelected);
											if (selectedPageIndex !== -1) {
												const selectedPageNumber = selectedPageIndex + 1;
												onMovePage({ action: "page", page: selectedPageNumber + 1, sourcePage: selectedPageNumber });
											}
										}
									}}
									title={selectedCount === 1 ? "Move selected page down" : "Select exactly one page to move down"}
									src={moveDownIcon}
									alt="Move Down"
									style={{
										opacity: selectedCount === 1 ? 1 : 0.5,
										cursor: selectedCount === 1 ? 'pointer' : 'not-allowed'
									}}
								/>

							</div>
							<div className="toolbarRight">

								<>
									<img
										className={`toolbarButton ${selectedCount === 0 ? 'disabled' : ''}`}
										onClick={selectedCount > 0 ? handleDeleteSelectedPages : undefined}
										title={selectedCount > 0 ? "Delete selected pages" : "Select pages to delete"}
										src={deleteIcon}
										alt="Delete"
										style={{
											opacity: selectedCount === 0 ? 0.5 : 1,
											cursor: selectedCount === 0 ? 'not-allowed' : 'pointer'
										}}
									/>
									<img
										className={`toolbarButton ${selectedCount >= 1 ? '' : 'disabled'}`}
										onClick={() => selectedCount > 0 && setShowReorderModal(true)}
										title={selectedCount > 0 ? "Reorder pages" : "Select pages to reorder"}
										src={reorderIcon}
										alt="Reorder"
										style={{
											opacity: selectedCount === 0 ? 0.5 : 1,
											cursor: selectedCount === 0 ? 'not-allowed' : 'pointer'
										}}
									/>
								</>


							</div>
						</div>
						<div className="templateInfo">
							<input
								type="checkbox"
								checked={selectedPages.every(Boolean) && selectedPages.length > 0}
								onChange={(e) => {
									if (e.target.checked) {
										selectAllPages();
									} else {
										deselectAllPages();
									}
								}}
							/> {templateName}
						</div>
						<div
							className="thumbnailsDropArea"
							onDragEnter={handleExternalFileDragEnter}
							onDragOver={handleExternalFileDragOver}
							onDragLeave={handleExternalFileDragLeave}
							onDrop={handleExternalFileDrop}
						>
							{!isDraggingExternalFile && (
								<div className="dropHintText">
									Drag files here to append pages
								</div>
							)}
							{isDraggingExternalFile && (
								<div className="fileDropOverlay">
									<div className="fileDropContent">
										<div className="fileDropIcon">📄</div>
										<div className="fileDropText">Drop file to append</div>
										<div className="fileDropHint">Will be added at the end of the document</div>
									</div>
								</div>
							)}
							<Thumbnails renderThumbnailItem={renderThumbnailItem} />
						</div>
					</div>
					</>,
				icon: <ThumbnailIcon />,
				title: 'Thumbnails',
			},
		],
	});



	const pageNavigationPluginInstance = pageNavigationPlugin();

	const currentPageRef = useRef(currentPage);

	// DocType dropdown state removed (unused in this view)
	// Escuchar zoomconst layout = defaultLayoutPlugin();
	const moveField = useCallback((id: string, b: Partial<BoundsInInches>) => {
		setFields((prev: FieldModel[]) =>
			prev.map((f: FieldModel) =>
				f.id === id ? { ...f, bounds: { ...f.bounds, ...b } } : f
			)
		);
	}, []);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { responseDocType1, errorDocType1, loading2 } = useSelector((state: RootState) => ({
		responseDocType1: (state as any).DocType?.responseDocType1,
		errorDocType1: (state as any).DocType?.errorDocType1,
		loading2: (state as any).DocType?.loadingDocType1,
	}));
	const [selectedPages, setSelectedPages] = React.useState<boolean[]>([]);
	const [totalPages, setTotalPages] = React.useState<number>(0);
	const [draggedPage, setDraggedPage] = React.useState<number | null>(null);
	const [dragOverPage, setDragOverPage] = React.useState<number | null>(null);
	const [hoveredPage, setHoveredPage] = React.useState<number | null>(null);
	const [isAutoScrolling, setIsAutoScrolling] = React.useState<boolean>(false);
	const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const [lastMouseY, setLastMouseY] = React.useState<number | null>(null);
	const [dragDirection, setDragDirection] = React.useState<'up' | 'down' | null>(null);
	const [dragSpeed, setDragSpeed] = React.useState<number>(0);
	const [showReorderModal, setShowReorderModal] = React.useState<boolean>(false);
	const [targetIndex, setTargetIndex] = React.useState<string>('');
	const [isDraggingExternalFile, setIsDraggingExternalFile] = React.useState<boolean>(false);
	const [droppedFiles, setDroppedFiles] = React.useState<File[]>([]);
	const [showAiConfirmDialog, setShowAiConfirmDialog] = React.useState<boolean>(false);

	const handleDocumentLoad = (e: DocumentLoadEvent) => {
		setSelectedPages(Array(e.doc.numPages).fill(false));
		setTotalPages(e.doc.numPages);
		setSelectedCount(0);
	};

	const selectAllPages = () => {
		const allSelected = Array(selectedPages.length).fill(true);
		setSelectedPages(allSelected);
		setSelectedCount(selectedPages.length);
	};

	const deselectAllPages = () => {
		const allDeselected = Array(selectedPages.length).fill(false);
		setSelectedPages(allDeselected);
		setSelectedCount(0);
	};

	const handleChoosePage = (e: React.ChangeEvent<HTMLInputElement>, pageIndex: number) => {
		const isSelected = e.target.checked;
		const updatedSelectedPages = [...selectedPages];
		updatedSelectedPages[pageIndex] = isSelected;
		setSelectedPages(updatedSelectedPages);

		// Update selected count
		const newSelectedCount = updatedSelectedPages.filter(Boolean).length;
		setSelectedCount(newSelectedCount);
	};

	// Drag and drop handlers
	const handleDragStart = (e: React.DragEvent, pageIndex: number) => {
		setDraggedPage(pageIndex);
		setLastMouseY(e.clientY); // Initialize mouse Y position tracking
		setDragDirection(null); // Reset drag direction
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', pageIndex.toString());
	};

	const handleDragOver = (e: React.DragEvent, pageIndex: number) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setDragOverPage(pageIndex);

		// Auto-scroll functionality based on drag direction
		if (draggedPage === null || lastMouseY === null) return;

		const currentMouseY = e.clientY;
		const mouseDelta = currentMouseY - lastMouseY;
		const movementThreshold = 5; // Minimum pixels of movement to trigger scroll

		// Determine drag direction based on mouse movement
		if (Math.abs(mouseDelta) > movementThreshold) {
			const newDirection = mouseDelta > 0 ? 'down' : 'up';
			const currentDragSpeed = Math.min(Math.abs(mouseDelta), 30); // Cap speed for reasonable scrolling

			setDragDirection(newDirection);
			setDragSpeed(currentDragSpeed);
			setLastMouseY(currentMouseY);

			// Get thumbnail container bounds
			const thumbnailsContainer = document.querySelector('.rpv-thumbnail__list-container');
			if (!thumbnailsContainer) return;

			const containerRect = thumbnailsContainer.getBoundingClientRect();
			const scrollZoneHeight = 80; // Increased zone for easier triggering

			// Check if we're near edges and should scroll based on drag direction


		}
	};

	const handleDragLeave = (e: React.DragEvent) => {
		// Only clear dragOverPage if we're actually leaving the thumbnail area
		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
		const x = e.clientX;
		const y = e.clientY;

		if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
			setDragOverPage(null);
			setLastMouseY(null);
			setDragDirection(null);
			setDragSpeed(0);
		}
	};

	const handleDrop = (e: React.DragEvent, targetPageIndex: number) => {
		e.preventDefault();

		if (draggedPage !== null && draggedPage !== targetPageIndex) {
			const sourcePageNumber = draggedPage + 1; // Convert to 1-based
			const targetPageNumber = targetPageIndex + 1; // Convert to 1-based

			console.log(`Dragging page ${sourcePageNumber} to position ${targetPageNumber}`);

			// Call onMovePage with both source and target information
			if (onMovePage) {
				onMovePage({
					action: "page",
					page: targetPageNumber,
					sourcePage: sourcePageNumber
				});
			}
		}

		setDraggedPage(null);
		setDragOverPage(null);
	};

	const handleDragEnd = () => {
		setDraggedPage(null);
		setDragOverPage(null);
		setLastMouseY(null);
		setDragDirection(null);
		setDragSpeed(0);
	};

	// File drop handlers (external files, not page reordering)
	const handleExternalFileDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		// Detectar si son archivos externos (no drag interno de páginas)
		if (e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('text/plain')) {
			setIsDraggingExternalFile(true);
		}
	};

	const handleExternalFileDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('text/plain')) {
			e.dataTransfer.dropEffect = 'copy';
			setIsDraggingExternalFile(true);
		}
	};

	const handleExternalFileDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		const thumbnailsContainer = e.currentTarget as HTMLElement;
		const rect = thumbnailsContainer.getBoundingClientRect();
		const x = e.clientX;
		const y = e.clientY;

		if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
			setIsDraggingExternalFile(false);
		}
	};

	const handleExternalFileDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		setIsDraggingExternalFile(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			// Guardar archivos y mostrar diálogo de confirmación AI
			setDroppedFiles(files);
			setShowAiConfirmDialog(true);
		}
	};

	const handleAiConfirm = (useAi: boolean) => {
		setShowAiConfirmDialog(false);
		if (droppedFiles.length > 0 && onFilesDrop) {
			onFilesDrop(droppedFiles, useAi);
		}
		setDroppedFiles([]);
	};

	const handleAiCancel = () => {
		setShowAiConfirmDialog(false);
		setDroppedFiles([]);
	};

	// Function to handle deleting selected pages
	const handleDeleteSelectedPages = () => {
		if (selectedCount === 0) return;

		// Get 1-based page numbers for selected pages
		const selectedPageNumbers = selectedPages
			.map((isSelected, index) => isSelected ? index + 1 : null)
			.filter((pageNum): pageNum is number => pageNum !== null);

		if (selectedPageNumbers.length > 0 && onDeletePage) {
			onDeletePage(selectedPageNumbers);
		}
	};

	// Function to handle reorder modal
	const handleReorderPages = () => {
		const targetPos = parseInt(targetIndex);
		if (isNaN(targetPos) || targetPos < 1 || targetPos > totalPages) {
			alert(`Please enter a valid position between 1 and ${totalPages}`);
			return;
		}

		// Get selected page numbers (1-based)
		const selectedPageNumbers = selectedPages
			.map((isSelected, index) => isSelected ? index + 1 : null)
			.filter((pageNum): pageNum is number => pageNum !== null);

		if (selectedPageNumbers.length > 0 && onMovePage) {
			// For multiple pages, we'll move them one by one to the target position
			selectedPageNumbers.forEach((pageNum, index) => {
				onMovePage({
					action: "page",
					page: targetPos + index, // Offset by index to maintain order
					sourcePage: pageNum
				});
			});
		}

		// Close modal and reset
		setShowReorderModal(false);
		setTargetIndex('');
	};

	const handleCloseReorderModal = () => {
		setShowReorderModal(false);
		setTargetIndex('');
	};

	const renderThumbnailItem = (props: RenderThumbnailItemProps) => {
		const isDragging = draggedPage === props.pageIndex;
		const isDragOver = dragOverPage === props.pageIndex;
		const isHovered = hoveredPage === props.pageIndex;

		return (
			<div
				key={props.pageIndex}
				className={`custom-thumbnail-item ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
				data-testid={`thumbnail-${props.pageIndex}`}
				draggable={true}
				onDragStart={(e) => handleDragStart(e, props.pageIndex)}
				onDragOver={(e) => handleDragOver(e, props.pageIndex)}
				onDragLeave={handleDragLeave}
				onDrop={(e) => handleDrop(e, props.pageIndex)}
				onDragEnd={handleDragEnd}
				onMouseEnter={() => setHoveredPage(props.pageIndex)}
				onMouseLeave={() => setHoveredPage(null)}
				style={{
					cursor: isDragging ? 'grabbing' : 'grab',
					padding: '0.5rem',
					width: '100%',
					position: 'relative',
					opacity: isDragging ? 0.5 : 1,
					backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
					border: isDragOver ? '2px dashed #1976d2' : '2px solid transparent',
					borderRadius: '4px',
					transition: 'all 0.2s ease',
				}}
			>
				<div style={{ marginBottom: '0.5rem' }} className="overlay" onClick={props.onJumpToPage}>
					{(isHovered || isDragging) && (
						<img
							className="dragIndicator"
							src={dragIcon}
							alt="Drag"
							draggable={false}
							style={{
								opacity: isHovered ? 1 : 0.8,
								transition: 'opacity 0.2s ease'
							}}
						/>
					)}
					<input
						type="checkbox"
						className="pageCheckbox"
						checked={selectedPages[props.pageIndex] || false}
						onChange={(e) => handleChoosePage(e, props.pageIndex)}
						onClick={(e) => e.stopPropagation()} // Prevent drag when clicking checkbox
					/>
					{props.renderPageThumbnail}
				</div>
				<div
					style={{
						alignItems: 'center',
						display: 'flex',
						justifyContent: 'center',
						margin: '0 auto',
						width: '100px',
					}}
				>
					<div style={{ textAlign: 'center' }}>{props.renderPageLabel}</div>
				</div>
			</div>
		);
	};

	const thumbnailPluginInstance = thumbnailPlugin();
	const { Thumbnails } = thumbnailPluginInstance;
	// Removed unused doc type list mapping (was not rendered anywhere)
	useEffect(() => {
		dispatch(
			getDocTypeList1(
				{
					page: 1,
					pageSize: 100,
					sortBy: "name",
					sortOrder: 1,
					filtered: true,
				},
				navigate
			)
		);
	}, [dispatch, navigate]);

	// Function to get current page from PDF viewer header
	const getCurrentPageFromHeader = useCallback(() => {
		if (!viewerContainerRef.current) return null;

		// Look for the page indicator in the PDF viewer header
		const pageIndicator = viewerContainerRef.current.querySelector('[data-testid="page-navigation__current-page-input"]') as HTMLInputElement;
		if (pageIndicator) {
			const pageNumber = parseInt(pageIndicator.value, 10);
			return isNaN(pageNumber) ? null : pageNumber;
		}

		// Alternative: look for text content that shows "X / Y" format
		const pageTextElements = Array.from(viewerContainerRef.current.querySelectorAll('span, div'));
		for (const element of pageTextElements) {
			const text = element.textContent;
			if (text && /\d+\s*\/\s*\d+/.test(text)) {
				const match = text.match(/(\d+)\s*\/\s*\d+/);
				if (match) {
					return parseInt(match[1], 10);
				}
			}
		}

		return null;
	}, []);

	// Effect to monitor page changes
	// Use requestAnimationFrame to avoid layout-thrashing inside ResizeObserver callbacks
	useEffect(() => {
		if (!setCurrentPage) return;

		let mounted = true;
		let timeoutId: NodeJS.Timeout;

		const checkPageChange = () => {
			if (!mounted) return;

			// Debounce the check to avoid excessive calls during scrolling
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				if (!mounted) return;

				// Use requestAnimationFrame to avoid synchronous reflows that can trigger
				// ResizeObserver loop warnings in some browsers
				requestAnimationFrame(() => {
					if (!mounted) return;

					try {
						const newPage = getCurrentPageFromHeader();
						if (newPage && newPage !== currentPageRef.current) {
							currentPageRef.current = newPage;
							setCurrentPage(newPage);
						}
					} catch (err) {
						// Silently handle ResizeObserver and other DOM errors
						const errorMessage = err instanceof Error ? err.message : String(err);
						if (!errorMessage?.includes('ResizeObserver')) {
							console.warn("checkPageChange error:", err);
						}
					}
				});
			}, 100); // Debounce by 100ms
		};

		// Check immediately
		checkPageChange();

		// Poll less frequently to reduce layout pressure
		const interval = setInterval(checkPageChange, 2000); // Increased to 2 seconds

		return () => {
			mounted = false;
			clearTimeout(timeoutId);
			clearInterval(interval);
		};
	}, [setCurrentPage, getCurrentPageFromHeader]);

	const pluginInstance = useMemo(
		() =>
			createFieldsPlugin(
				fields,
				moveField,
				setSelectedId,
				selectedId,
				onContextMenu,
				groups,
				placingField && onPlaceField
					? { enabled: true, onPlace: onPlaceField, cursor: "crosshair" }
					: undefined,
				highlightedFieldId
			),
		[fields, moveField, selectedId, onContextMenu, groups, placingField, onPlaceField, highlightedFieldId]
	);

	const { responseTree } = useSelector((state: RootState) => ({
		responseTree: state.Tree.responseTree,
	}));

	// Get selected page numbers for display in modal
	const selectedPageNumbers = selectedPages
		.map((isSelected, index) => isSelected ? index + 1 : null)
		.filter((pageNum): pageNum is number => pageNum !== null);

	return (
		<>
			{/* Reorder Modal */}
			<Dialog open={showReorderModal} onClose={handleCloseReorderModal} maxWidth="sm" fullWidth>
				<DialogTitle>Reorder Pages</DialogTitle>
				<DialogContent>
					<Typography variant="body1" style={{ marginBottom: '16px' }}>
						Selected pages: {selectedPageNumbers.join(', ')}
					</Typography>
					<Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
						Move the selected page{selectedPageNumbers.length > 1 ? 's' : ''} to a specific position.
					</Typography>
					<TextField
						autoFocus
						margin="dense"
						label="Target Position"
						type="number"
						fullWidth
						variant="outlined"
						value={targetIndex}
						onChange={(e) => setTargetIndex(e.target.value)}
						helperText={`Enter a position between 1 and ${totalPages}`}
						inputProps={{
							min: 1,
							max: totalPages
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseReorderModal} color="secondary">
						Cancel
					</Button>
					<Button 
						onClick={handleReorderPages} 
						color="primary" 
						variant="contained"
						disabled={!targetIndex || selectedPageNumbers.length === 0}
					>
						Move Pages
					</Button>
				</DialogActions>
			</Dialog>

			{/* AI Confirmation Dialog */}
			{showAiConfirmDialog && (
				<div className="aiConfirmOverlay">
					<div className="aiConfirmDialog">
						<h3>Append {droppedFiles.length} file(s)</h3>
						<p>Do you want to use AI to automatically detect fields?</p>
						<div className="aiConfirmButtons">
							<button className="aiConfirmBtn primary" onClick={() => handleAiConfirm(true)}>
								Yes, use AI
							</button>
							<button className="aiConfirmBtn secondary" onClick={() => handleAiConfirm(false)}>
								No, just append
							</button>
							<button className="aiConfirmBtn cancel" onClick={handleAiCancel}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}

			<div className={classes.documentEditorContainer}>
			{/* PDF Viewer Column - 5/8 */}
			<div
				className={classes.pdfViewerContainer}
				ref={viewerContainerRef}
				onClick={() => setSelectedId(null)}
			>
				<Worker
					workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/legacy/build/pdf.worker.js`}
				>
					<Viewer
						defaultScale={1}
						fileUrl={fileUrl}
						plugins={[layout, pageNavigationPluginInstance, pluginInstance, thumbnailPluginInstance]}
						onDocumentLoad={(e: DocumentLoadEvent) => {
							const n = e?.doc?.numPages || 0;
							if (onNumPages) onNumPages(n);
							handleDocumentLoad(e);
							const { activateTab } = layout;
							activateTab(0); // Thumbnails

						}}

					/>
				</Worker>
			</div>

			{/* Inspector Section Column - 3/8 */}
			{showInspector && (
				<div className={classes.inspectorSectionContainer}>
					{/* Path Tree Container */}
					<div className={classes.pathTreeContainer} id="default-folder-section">
						<div className="col-sm-12">
							<div className={classes.saveToCard}>
								<div className={classes.saveToHeader}>
									<div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
										<Typography variant="subtitle2" style={{ fontWeight: 700 }}>
											Save to
										</Typography>
										<Tooltip title="Required before saving">
											<span className={classes.requiredDot} />
										</Tooltip>
									</div>

									{selectedFolder && (
										<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
											<Button
												size="small"
												variant="text"
												onClick={() => setShowFolderId((v) => !v)}
												style={{ textTransform: "none" }}
											>
												{showFolderId ? "Hide ID" : "Show ID"}
											</Button>
										</div>
									)}
								</div>

								{!selectedFolder ? (
									<div className={classes.saveToEmpty} onClick={openFolderPicker}>
										<div style={{ textAlign: "center" }}>
											<FolderOpenIcon style={{ opacity: 0.7, marginBottom: 6 }} />
											<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
												Choose a folder
											</Typography>
											<Typography variant="caption" style={{ color: "rgba(0,0,0,0.6)" }}>
												Processed files will be saved here
											</Typography>
										</div>
									</div>
								) : (
									<>
										<div className={classes.saveToFilledRow}>
											<div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
												<div className={classes.folderAvatar}>
													<FolderOpenIcon fontSize="small" style={{ color: "rgba(0, 106, 198, 1)" }} />
												</div>
												<div className={classes.folderText}>
													<Typography variant="subtitle2" style={{ fontWeight: 700 }} noWrap>
														{(selectedFolderDisplay || "").split(" / ").slice(-1)[0] || "Folder"}
													</Typography>
													<Typography variant="caption" style={{ color: "rgba(0,0,0,0.6)" }} noWrap>
														{selectedFolderDisplay || selectedFolder}
													</Typography>
												</div>
											</div>
											<div className={classes.actionIcons}>
												<Tooltip title="Open">
													<IconButton size="small" onClick={openFolderPicker}>
														<OpenInNewIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Change folder">
													<IconButton size="small" onClick={openFolderPicker}>
														<AutorenewIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										</div>

										{showFolderId && (
											<Box mt={1} display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 8 }}>
												<Typography variant="caption" style={{ wordBreak: "break-all", color: "rgba(0,0,0,0.55)" }}>
													{selectedFolder}
												</Typography>
												<Button
													size="small"
													variant="text"
													onClick={() => navigator.clipboard?.writeText?.(selectedFolder)}
													style={{ textTransform: "none" }}
												>
													Copy
												</Button>
											</Box>
										)}
									</>
								)}

								{/* Hidden legacy PathTree trigger (keeps the existing folder picker dialog) */}
								<div style={{ position: "absolute", left: -99999, top: -99999 }}>
									<PathTree
										emptyPath={emptyPath}
										openButtonId="template-builder-default-folder-open"
										folderName={selectedFolderDisplay || selectedFolder || undefined}
										tree={responseTree ? responseTree : []}
										hidenReservedFolder={true}
										callBack={(folder: any) => {
											setSelectedFolder?.(folder.value);
											setSelectedFolderDisplay?.(folder.label ?? "");
										}}
									/>
								</div>
							</div>
						</div>
					</div>

					{/* Inspector Container */}
					<div className={classes.inspectorContainer}>
						{!selectedField ? (
							<div className={classes.emptyInspector}>
								<Typography variant="subtitle2" style={{ fontWeight: 600 }}>
									Select a field to edit
								</Typography>
								<Typography variant="body2" style={{ color: "rgba(0,0,0,0.65)", marginTop: 4 }}>
									Click a field on the document, or pick one from the list below.
								</Typography>
								<div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
									<Button
										variant="contained"
										color="primary"
										onClick={() => onRequestAddField?.()}
										disabled={!onRequestAddField}
									>
										Add field
									</Button>
								</div>
								{fields.length > 0 && (
									<div className={classes.emptyInspectorList}>
										{fields.slice(0, 12).map((f) => (
											<Button
												key={f.id}
												variant="text"
												onClick={() => setSelectedId(f.id)}
												style={{ justifyContent: "flex-start", textTransform: "none" }}
											>
												{f.title || "Untitled"}{" "}
												<span style={{ opacity: 0.55, marginLeft: 6 }}>({f.type})</span>
											</Button>
										))}
										{fields.length > 12 && (
											<Typography variant="caption" style={{ color: "rgba(0,0,0,0.6)" }}>
												…and {fields.length - 12} more
											</Typography>
										)}
									</div>
								)}
							</div>
						) : (
							<Inspector
								field={selectedField}
								update={(p) =>
									setFields((prev) =>
										prev.map((f) => (f.id === selectedId ? { ...f, ...p } : f))
									)
								}
								onDelete={() => {
									setFields((prev) => prev.filter((f) => f.id !== selectedId));
									setSelectedId(null);
								}}
								onClose={() => setSelectedId(null)}
								groups={groups}
								onGroupsChange={onGroupsChange}
								templateId={templateId}
								allFields={fields}
								onAssignToGroup={(fieldId, groupId) => {
									const group = groups.find(g => g.id === groupId);
									const fieldGroup = group ? group.name : undefined;
									setFields((prev) =>
										prev.map((f) => (f.id === fieldId ? { ...f, fieldGroup } : f))
									);
								}}
							/>
						)}
					</div>
					{/* Save/Send actions live in the top consolidated header bar */}
				</div>
			)}
		</div>
		</>
	);
};

export default DocumentEditor;
